'use client'

import { useForm } from 'react-hook-form'

import { FilterPopoverButton } from '~/app/accounting/filters/FilterPopoverButton'
import {
  RangeInputs,
  useRangeFieldErrors,
} from '~/app/accounting/filters/RangeInputs'
import { RadioGroupInput } from '~/components/component-library'

export type LotStatus = 'open' | 'closed' | 'all'

export type RemainingFilterValue = {
  status: LotStatus
  min: string
  max: string
}

// What the view opens with: only lots that still hold inventory.
export const DEFAULT_REMAINING_FILTER: RemainingFilterValue = {
  status: 'open',
  min: '',
  max: '',
}

const CLEARED_REMAINING_FILTER: RemainingFilterValue = {
  status: 'all',
  min: '',
  max: '',
}

const STATUS_LABELS: Record<LotStatus, string> = {
  open: 'Open',
  closed: 'Closed',
  all: '',
}

const isEmpty = (value: RemainingFilterValue) =>
  value.status === 'all' && value.min === '' && value.max === ''

// Bounds on a lot's remaining amount, denominated in the drilled-in token
// (no currency prefix).
export const RemainingFilterButton = ({
  value,
  onChange,
}: {
  value: RemainingFilterValue
  onChange: (value: RemainingFilterValue) => void
}) => {
  const form = useForm<RemainingFilterValue>({ values: value })
  const { status, min, max } = form.watch()

  const { minError, maxError } = useRangeFieldErrors({
    min,
    max,
    setError: form.setError,
    clearErrors: form.clearErrors,
  })

  const hasChanged =
    min !== value.min || max !== value.max || status !== value.status

  const boundsLabel =
    value.min !== '' && value.max !== ''
      ? `${value.min}-${value.max}`
      : value.min !== ''
        ? `>${value.min}`
        : value.max !== ''
          ? `<${value.max}`
          : ''
  const statusLabel = STATUS_LABELS[value.status]
  const activeLabel =
    statusLabel && boundsLabel
      ? `${statusLabel}: ${boundsLabel}`
      : statusLabel || boundsLabel

  return (
    <FilterPopoverButton
      label="Remaining"
      activeLabel={activeLabel}
      isAppliedEmpty={isEmpty(value)}
      isLocalEmpty={isEmpty({ status, min, max })}
      isInvalid={!!minError || !!maxError}
      hasChanged={hasChanged}
      onApply={() => onChange({ status, min, max })}
      onReset={() => {
        form.reset(CLEARED_REMAINING_FILTER)
        onChange(CLEARED_REMAINING_FILTER)
      }}
      onClear={() => {
        form.reset(CLEARED_REMAINING_FILTER)
        onChange(CLEARED_REMAINING_FILTER)
      }}
      onClose={() => {
        if (hasChanged) form.reset(value)
      }}
    >
      {(focusAction) => (
        <>
          <RadioGroupInput
            control={form.control}
            inputName="status"
            label="Status"
            size="small"
            options={[
              { value: 'open', label: 'Open' },
              { value: 'closed', label: 'Closed' },
              { value: 'all', label: 'All' },
            ]}
          />
          <RangeInputs
            control={form.control}
            minName="min"
            maxName="max"
            onComplete={focusAction}
          />
        </>
      )}
    </FilterPopoverButton>
  )
}
